import React, { useEffect, useRef } from 'react';

// Animated orb gradient field behind the home hero. The orbs don't just drift —
// they *breathe* to a slow, music-like pulse: each one swells in radius and
// brightness on its own offset, so the whole backdrop feels alive and sonic
// without any literal waveform or icon placed on top of it.
export function HeroCanvas() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let rafId = 0;
    let t = 0;

    // Slow base tempo of the "breath" (radians per frame ≈ a ~4.5s pulse at 60fps).
    const TEMPO = 0.024;

    const ORBS = [
      { cx: 0.80, cy: 0.10, r: 0.55, c: [230, 84, 58], p: 0, s: 0.00018, beat: 0.0 },
      { cx: 0.10, cy: 0.85, r: 0.50, c: [38, 136, 199], p: Math.PI, s: 0.00022, beat: Math.PI * 0.5 },
      { cx: 0.42, cy: 0.48, r: 0.38, c: [211, 137, 184], p: Math.PI / 2, s: 0.00013, beat: Math.PI },
      { cx: 0.18, cy: 0.22, r: 0.28, c: [246, 148, 36], p: Math.PI * 1.5, s: 0.00027, beat: Math.PI * 1.5 },
    ];

    function resize() {
      const hero = canvas!.closest('.hero') as HTMLElement | null;
      canvas!.width = hero ? hero.offsetWidth : window.innerWidth;
      canvas!.height = hero ? hero.offsetHeight : window.innerHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    function draw() {
      const w = canvas!.width, h = canvas!.height;
      ctx!.fillStyle = '#0e0a09';
      ctx!.fillRect(0, 0, w, h);
      ORBS.forEach((o) => {
        // Drift (unchanged) + a slow rhythmic breath. The breath blends a main
        // swell with a softer octave so it reads like a pulse, not a flat sine.
        const ox = o.cx + Math.sin(t * o.s + o.p) * 0.14;
        const oy = o.cy + Math.cos(t * o.s * 0.7 + o.p) * 0.10;

        const swell = Math.sin(t * TEMPO + o.beat);
        const octave = Math.sin(t * TEMPO * 2 + o.beat);
        const env = (swell * 0.7 + octave * 0.3 + 1) / 2; // 0..1

        const x = ox * w, y = oy * h;
        const r = o.r * Math.min(w, h) * (0.9 + env * 0.18); // radius breathes ±~14%
        const peak = 0.16 + env * 0.12;                      // center alpha pulses
        const [rv, gv, bv] = o.c;
        const g = ctx!.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0, `rgba(${rv},${gv},${bv},${peak.toFixed(3)})`);
        g.addColorStop(0.5, `rgba(${rv},${gv},${bv},${(peak * 0.36).toFixed(3)})`);
        g.addColorStop(1, `rgba(${rv},${gv},${bv},0)`);
        ctx!.fillStyle = g;
        ctx!.fillRect(0, 0, w, h);
      });
    }

    function loop() {
      t++;
      draw();
      rafId = requestAnimationFrame(loop);
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      draw(); // single static frame, no pulsing
    } else {
      loop();
    }

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={ref} className="hero-bg" />;
}
